// Sub-pantalla de una factura de la suscripción (solo AR) — réplica de la vista de
// factura de Stripe, con diseño Atlas. Full-screen, sin el shell de la app.
// Card "Factura pagada" (monto + descargar factura/recibo) + panel de detalle
// (RESUMEN / ÍTEMS / Historial de pagos) que se abre con "Ver detalles".
// Ruta: /facturacion/factura/:id. Datos MOCK en Fase 1 (lib/facturacionMock.ts);
// la descarga del PDF es placeholder hasta el backend (Fase 2).
// Ver ADR-2026-08-03-facturacion-pantalla-propia-fullscreen y, para el "Volver",
// ADR-2026-08-07-facturacion-volver-loop.

import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, CheckCircle2, ChevronRight, Download, Lock, Mail } from 'lucide-react';
import { supabase } from '../lib/supabase';
import OptionSheet from '../components/opciones/OptionSheet';
import { CardBrandIcon, detectCardBrand } from '../components/CardBrandIcon';
import { getFacturaMock, type FacturaMock } from '../lib/facturacionMock';
import { descargarFacturaPDF, enviarFacturaPorMail } from '../lib/paymentServer';

const MONEDA = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
// Fechas date-only ('YYYY-MM-DD', ej. fecha_comprobante) se parsean como LOCAL, no UTC, para
// que en Argentina (UTC-3) no se muestren un día antes.
function parseFechaLocal(s: string | null): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
const fmtLarga = (iso: string) => {
  const d = parseFechaLocal(iso);
  return d ? d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
};
const fmtCorta = (iso: string) => {
  const d = parseFechaLocal(iso);
  return d ? d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
};

export default function FacturaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // El mock de demo solo aplica si venimos del portal en modo demo (?demo).
  const [searchParams] = useSearchParams();
  const demo = searchParams.get('demo') !== null;
  // undefined = cargando, null = no encontrada.
  const [factura, setFactura] = useState<FacturaMock | null | undefined>(undefined);
  const [verDetalle, setVerDetalle] = useState(false);
  const [avisoDescarga, setAvisoDescarga] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [razonSocial, setRazonSocial] = useState<string | null>(null);
  // Para el botón "Enviar al mail": sin email de facturación cargado no se muestra, porque no
  // habría a dónde mandarla.
  const [userId, setUserId] = useState<string | null>(null);
  const [emailFacturacion, setEmailFacturacion] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  // `demo` en la URL NO alcanza para saber que la factura es de mentira: con `?demo` y el id de una
  // factura REAL (un link copiado, por ejemplo) la pantalla muestra la factura de verdad. Esto se
  // enciende solo cuando lo que se está mostrando salió de los ejemplos.
  const [esFacturaDeEjemplo, setEsFacturaDeEjemplo] = useState(false);
  const [avisoEnvio, setAvisoEnvio] = useState<{ ok: boolean; texto: string } | null>(null);

  useEffect(() => {
    (async () => {
      // EL USUARIO PRIMERO, antes de mostrar nada. La pantalla habilita "Descargar" apenas hay
      // factura, y ese botón manda el user_id para que el server verifique dueño. Si el usuario se
      // resolviera después, habría un instante —corto, pero real en conexiones lentas— con el botón
      // activo y sin usuario: la descarga saldría sin el dato y el candado quedaría sin aplicar.
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id ?? null;
      setUserId(uid);

      // 1. Mock SOLO en modo demo (fuera de demo, un id mock NO debe mostrarse como factura
      //    real). 2. Si no, la factura real de facturas_suscripcion por id.
      const mock = demo ? getFacturaMock(id) : undefined;
      if (mock) {
        setFactura(mock);
        setEsFacturaDeEjemplo(true);
      } else if (id) {
        const { data: r } = await supabase
          .from('facturas_suscripcion')
          .select('id, fecha_comprobante, periodo_desde, periodo_hasta, letra, punto_venta, numero_comprobante, neto, iva, total, estado')
          .eq('id', id)
          .maybeSingle();
        if (r) {
          const row = r as {
            id: string;
            fecha_comprobante: string | null;
            periodo_desde: string | null;
            periodo_hasta: string | null;
            letra: string | null;
            punto_venta: number | null;
            numero_comprobante: number | null;
            neto: number | string | null;
            iva: number | string | null;
            total: number | string | null;
            estado: string;
          };
          setFactura({
            id: row.id,
            fecha: row.fecha_comprobante ?? '',
            periodoDesde: row.periodo_desde ?? row.fecha_comprobante ?? '',
            periodoHasta: row.periodo_hasta ?? '',
            tipo: (row.letra as FacturaMock['tipo']) ?? 'B',
            numero: `${String(row.punto_venta ?? 0).padStart(4, '0')}-${String(row.numero_comprobante ?? 0).padStart(8, '0')}`,
            plan: '',
            neto: Number(row.neto ?? 0),
            iva: Number(row.iva ?? 0),
            total: Number(row.total ?? 0),
            estado: (row.estado as FacturaMock['estado']) ?? 'emitida',
            metodo: '',
          });
        } else {
          setFactura(null);
        }
      } else {
        setFactura(null);
      }

      // "Para" del resumen: razón social del usuario.
      if (!uid) return;
      const { data: p } = await supabase.from('profiles').select('id_company').eq('id', uid).maybeSingle();
      const idc = (p as { id_company?: string | null } | null)?.id_company;
      if (!idc) return;
      const { data: df } = await supabase
        .from('datos_facturacion_suscripcion')
        .select('razon_social, email_facturacion')
        // `activo` importa: la tabla guarda el histórico y `maybeSingle()` FALLA si vuelve más de
        // una fila. Hoy nadie tiene dos (verificado), pero el día que alguien corrija sus datos
        // esta pantalla dejaría de mostrar la razón social sin que nadie entienda por qué.
        .eq('activo', true)
        .eq('id_company', idc)
        .maybeSingle();
      const datos = df as { razon_social?: string | null; email_facturacion?: string | null } | null;
      setRazonSocial(datos?.razon_social ?? null);
      setEmailFacturacion(datos?.email_facturacion?.trim() || null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, demo]);

  // El "Volver" tiene que SACAR del historial la entrada del detalle (POP), no apilar otra
  // `/facturacion` encima: si apila, el "Volver" del portal (que es `navigate(-1)`) cae justo
  // en el detalle que quedó debajo y el par de botones se hace un loop infinito.
  // Mismo criterio que ADR-144: POP cuando hay historial in-app, ruta por fallback si no.
  // El fallback va con `replace` a propósito — un push ahí recrearía el mismo loop.
  const volver = () => {
    if (location.key !== 'default') {
      navigate(-1);
      return;
    }
    navigate(`/facturacion${demo ? '?demo' : ''}`, { replace: true, state: { sinHistorial: true } });
  };

  const descargar = async () => {
    if (!factura || descargando) return;
    // Cinturón, además del orden de carga: sin usuario la descarga saldría sin el dato que el
    // server usa para verificar dueño, y cuando ese dato pase a ser obligatorio fallaría con un
    // error del server. Mejor decirlo acá. Pasa si la sesión venció mientras la pantalla estaba
    // abierta.
    if (!userId) {
      setAvisoDescarga(true);
      return;
    }
    setDescargando(true);
    try {
      await descargarFacturaPDF(factura.id, `Factura ${factura.tipo}-${factura.numero}`, userId);
      setAvisoDescarga(false);
    } catch {
      // Factura de ejemplo (demo) o backend todavía no activo.
      setAvisoDescarga(true);
    } finally {
      setDescargando(false);
    }
  };

  const enviarAlMail = async () => {
    if (!factura || enviando) return;
    setEnviando(true);
    setAvisoEnvio(null);
    // `finally`: sin esto, cualquier salida temprana deja el botón en "Enviando…" para siempre.
    try {
      if (esFacturaDeEjemplo) {
        // Las facturas de ejemplo no existen en la base. El botón se ve y se comporta igual, pero
        // NO manda nada — y lo dice, para que nadie crea que le llegó un mail que nunca salió.
        await new Promise((r) => setTimeout(r, 700));
        setAvisoEnvio({ ok: true, texto: 'Así se ve cuando sale. Esto es una demo: no se envió ningún mail.' });
        return;
      }
      if (!userId) {
        setAvisoEnvio({ ok: false, texto: 'Volvé a iniciar sesión y probá de nuevo.' });
        return;
      }
      const r = await enviarFacturaPorMail(factura.id, userId);
      setAvisoEnvio(r.ok ? { ok: true, texto: `Te la enviamos a ${r.destinatario}` } : { ok: false, texto: r.mensaje });
    } finally {
      setEnviando(false);
    }
  };

  if (factura === undefined) {
    return <div className="min-h-screen bg-surface-bg" />;
  }
  if (!factura) {
    return (
      <div className="min-h-screen bg-surface-bg flex flex-col items-center justify-center gap-4 px-6 text-center">
        <FileText className="w-10 h-10 text-neutral-400" />
        <p className="text-[15px] text-brand-black/80">No encontramos esa factura.</p>
        <button onClick={volver} className="inline-flex items-center gap-1.5 text-[14px] font-medium text-brand-primary">
          <ArrowLeft className="w-4 h-4" /> Volver a facturación
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-bg">
      {/* Mismo caso que el portal: pantalla fuera del shell, el "Volver" arrancaba
          debajo de la status bar del standalone. */}
      <div className="max-w-md mx-auto px-5 pb-5 pt-[calc(env(safe-area-inset-top,0px)+1.25rem)] md:py-10">
        <button
          onClick={volver}
          className="inline-flex items-center gap-1.5 h-9 rounded-md text-[14px] font-medium text-brand-black/70 hover:text-brand-black transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        <p className="text-center text-[15px] font-semibold text-brand-black/80 mt-6 mb-4">Atlas</p>

        <div className="rounded-2xl bg-surface-card border border-black/[0.07] shadow-[0_2px_16px_rgba(0,0,0,0.05)] px-6 py-7">
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-3">
              <FileText className="w-12 h-12 text-neutral-300" strokeWidth={1.5} />
              <CheckCircle2 className="w-5 h-5 text-success bg-surface-card rounded-full absolute -bottom-0.5 -right-0.5" />
            </div>
            <p className="text-[13.5px] text-neutral-500">Factura pagada</p>
            <p className="text-[30px] leading-none font-semibold text-brand-black/90 mt-1 tabular-nums">
              {MONEDA.format(factura.total)}
            </p>
            <button
              onClick={() => setVerDetalle(true)}
              className="inline-flex items-center gap-0.5 text-[13.5px] text-brand-primary mt-2 hover:opacity-80 transition-opacity"
            >
              Ver detalles de la factura <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-6 border-t border-neutral-100 pt-4 flex flex-col gap-2.5">
            <Row label="Número de factura" value={factura.numero} />
            <Row label="Fecha de pago" value={fmtLarga(factura.fecha)} />
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13.5px] text-neutral-500">Método de pago</span>
              <span className="flex items-center gap-2 text-[13.5px] text-brand-black/85">
                <CardBrandIcon brand={detectCardBrand(factura.metodo)} /> {factura.metodo}
              </span>
            </div>
            <Row label="Comprobante" value={`Factura ${factura.tipo}`} />
          </div>

          <div className="mt-6 flex flex-col gap-2">
            <button onClick={descargar} disabled={descargando} className={`${BTN_PRIMARY} w-full`}>
              <Download className="w-4 h-4" /> {descargando ? 'Generando…' : 'Descargar factura'}
            </button>
            {/* Solo con email de facturación cargado: si no, no hay a dónde mandarla. Con una
                factura de ejemplo se muestra igual, que es el punto de la demo. */}
            {(esFacturaDeEjemplo || (emailFacturacion && userId)) && (
              <button onClick={enviarAlMail} disabled={enviando} className={`${BTN_SECONDARY} w-full`}>
                <Mail className="w-4 h-4" /> {enviando ? 'Enviando…' : 'Enviar al mail'}
              </button>
            )}
          </div>

          {avisoEnvio && (
            <div
              className={`flex items-start gap-2 mt-3 rounded-lg px-3 py-2.5 text-[12.5px] ${
                avisoEnvio.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-neutral-100 text-neutral-600'
              }`}
            >
              {avisoEnvio.ok ? (
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              ) : (
                <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              )}
              <span>{avisoEnvio.texto}</span>
            </div>
          )}

          {avisoDescarga && (
            <Nota>No se pudo generar el PDF ahora. Si es una factura de ejemplo (demo) o el backend todavía no está activo, va a estar disponible con las facturas reales.</Nota>
          )}
        </div>

        <p className="text-center text-[12px] text-neutral-400 mt-6">Atlas · Facturación de tu plan</p>
      </div>

      {/* Panel de detalle (desktop: derecha / mobile: hoja abajo) */}
      <OptionSheet
        open={verDetalle}
        onClose={() => setVerDetalle(false)}
        title={`Se pagó el ${fmtCorta(factura.fecha)}`}
        acceptLabel="Listo"
      >
        <div className="flex flex-col gap-6 p-1">
          {/* Resumen */}
          <section>
            <p className="text-[11.5px] font-semibold text-neutral-400 uppercase tracking-wide mb-2">Resumen</p>
            <div className="flex flex-col gap-2">
              <Row label="Para" value={razonSocial || '—'} />
              <Row label="De" value="Atlas" />
              <Row label="Factura" value={`Nro. ${factura.numero}`} />
            </div>
          </section>

          {/* Ítems */}
          <section>
            <p className="text-[11.5px] font-semibold text-neutral-400 uppercase tracking-wide mb-2">Ítems</p>
            <p className="text-[12px] text-neutral-500 mb-2">
              {fmtCorta(factura.periodoDesde)} — {fmtCorta(factura.periodoHasta)}
            </p>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[14px] text-brand-black/90">Plan {factura.plan}</p>
                <p className="text-[12px] text-neutral-500">Cantidad 1</p>
              </div>
              <span className="text-[14px] text-brand-black/90 tabular-nums">{MONEDA.format(factura.neto)}</span>
            </div>
            <div className="mt-3 border-t border-neutral-100 pt-3 flex flex-col gap-2">
              <Row label="Neto gravado" value={MONEDA.format(factura.neto)} />
              <Row label="IVA (21%)" value={MONEDA.format(factura.iva)} />
              <Row label="Importe pagado" value={MONEDA.format(factura.total)} strong />
              <Row label="Importe restante" value={MONEDA.format(0)} />
            </div>
          </section>

          {/* Historial de pagos */}
          <section>
            <p className="text-[11.5px] font-semibold text-neutral-400 uppercase tracking-wide mb-2">Historial de pagos</p>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[14px] text-brand-black/90 tabular-nums">{MONEDA.format(factura.total)}</p>
                <p className="text-[12px] text-neutral-500">{factura.metodo}</p>
              </div>
              <span className="text-[13px] text-neutral-500">{fmtCorta(factura.fecha)}</span>
            </div>
          </section>
        </div>
      </OptionSheet>
    </div>
  );
}

const BTN_PRIMARY =
  'inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-medium shadow-sm transition-colors';

// Misma geometría EXACTA que el primario (alto, padding, radio, tipografía) y color neutro: es una
// acción secundaria al lado de "Descargar", no una segunda acción principal compitiendo con ella.
const BTN_SECONDARY =
  'inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg bg-white border border-neutral-200 hover:bg-neutral-50 text-brand-black/85 text-[14px] font-medium transition-colors';

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={`text-[13.5px] ${strong ? 'font-semibold text-brand-black/90' : 'text-neutral-500'}`}>{label}</span>
      <span
        className={`text-[13.5px] text-right ${strong ? 'font-semibold text-brand-black/90' : 'text-brand-black/85'}`}
      >
        {value}
      </span>
    </div>
  );
}

function Nota({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 mt-4 rounded-lg bg-neutral-100 px-3 py-2.5 text-[12.5px] text-neutral-600">
      <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}
